import * as React from "react";
import { Disclosure, Transition } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import { cn } from "@utils/cn";

interface AccordionProps {
  type: "single" | "multiple";
  children: React.ReactNode;
  className?: string;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
}

interface AccordionItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

interface AccordionTriggerProps {
  children: React.ReactNode;
  className?: string;
  open?: boolean; // Add open prop
}

interface AccordionContentProps {
  children: React.ReactNode;
  className?: string;
  open?: boolean; // Add open prop
}

const Accordion = ({ children, className }: AccordionProps) => {
  // For simplicity, this basic implementation only supports "multiple" type
  // as Headless UI Disclosure is a single component.
  // A full "single" implementation would require managing state for all items
  // and ensuring only one is open at a time, likely with React Context.
  // For now, we'll treat it as a container for multiple Disclosure items.
  return <div className={cn("space-y-2", className)}>{children}</div>;
};

const AccordionItem = ({ children, className }: AccordionItemProps) => {
  return (
    <div className={cn("border-b", className)}>
      <Disclosure>
        {({ open }) => (
          <>
            {React.Children.map(children, child => {
              if (React.isValidElement(child) && (child.type as any).displayName === "AccordionTrigger") {
                return React.cloneElement(child as React.ReactElement<AccordionTriggerProps>, { open, ...(child.props || {}) });
              }
              if (React.isValidElement(child) && (child.type as any).displayName === "AccordionContent") {
                return React.cloneElement(child as React.ReactElement<AccordionContentProps>, { open, ...(child.props || {}) });
              }
              return child;
            })}
          </>
        )}
      </Disclosure>
    </div>
  );
};
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps & { open?: boolean }
>(({ className, children, open, ...props }, ref) => (
  <Disclosure.Button
    ref={ref}
    className={cn(
      "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
      className
    )}
    {...props}
  >
    {children}
    <ChevronDown
      className={cn("h-4 w-4 shrink-0 transition-transform duration-200", open ? "rotate-180" : "")}
    />
  </Disclosure.Button>
));
AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  AccordionContentProps & { open?: boolean }
>(({ className, children, open, ...props }, ref) => (
  <Transition
    show={open}
    enter="transition duration-100 ease-out"
    enterFrom="transform scale-95 opacity-0"
    enterTo="transform scale-100 opacity-100"
    leave="transition duration-75 ease-out"
    leaveFrom="transform scale-100 opacity-100"
    leaveTo="transform scale-95 opacity-0"
  >
    <Disclosure.Panel
      ref={ref}
      static
      className={cn("pb-4 pt-0 overflow-hidden text-sm", className)}
      {...props}
    >
      {children}
    </Disclosure.Panel>
  </Transition>
));
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
